import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { getAPIUrl } from "../../../../../config/api";
import { ToastContext } from "../../../../../context/ToastContext";
import { parseDate } from "../../../../../helpers/date";
import { authSelector } from "../../../../../store/slices/auth";
import { userSelector } from "../../../../../store/slices/user";
import { ISourceDataStatus } from "../../../../../types/baseRateFile";

const DATA_SET_LABELS: Record<keyof ISourceDataStatus, string> = {
  addendumB: "Addendum B",
  addendumAA: "Addendum AA",
  clfs: "CLFS",
  asp: "ASP",
  dmepos: "DMEPOS",
  pprvu: "MPFS (PPRRVU)",
  gpci: "GPCI Addendum E (Locality Data)",
  ipps: "IPPS Table 5 FR (MS-DRG Data)",
  ippsStdAmounts: "IPPS Tables 1A-1E (Standardized Amounts)",
  ippsImpact: "IPPS Impact File (Hospital Data)",
  ippsPsfInpatient: "IPPS PSF Inpatient (CMS API)",
  ascWageIndex: "ASC Wage Index",
};

interface ITableRowData {
  id: string;
  dataSetKey: keyof ISourceDataStatus;
  dataSetLabel: string;
  year: number;
  /** Calendar quarter (1-4) for sources fetched per-quarter (e.g., PSF). null for the rest. */
  quarter: number | null;
  lastUpdated: Date | null;
  count: number;
}

interface ISourceDataTableProps {
  sourceDataStatus: ISourceDataStatus | null;
}

function SourceDataTable({ sourceDataStatus }: ISourceDataTableProps) {
  const { current: toastElement } = useContext(ToastContext);
  const { token } = useSelector(authSelector);
  const { currentAccount } = useSelector(userSelector);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const tableData = useMemo(() => {
    if (!sourceDataStatus) return [];

    const data: ITableRowData[] = [];

    (Object.keys(DATA_SET_LABELS) as (keyof ISourceDataStatus)[]).forEach(
      (key) => {
        const yearInfoArray = sourceDataStatus[key];

        if (yearInfoArray && yearInfoArray.length > 0) {
          // Sort by year descending; for per-quarter sources, also by quarter
          // descending within the same year (so 2026 Q2 sits above 2026 Q1).
          const sortedYearInfo = [...yearInfoArray].sort((a, b) => {
            if (a.year !== b.year) return b.year - a.year;
            return (b.quarter ?? 0) - (a.quarter ?? 0);
          });

          sortedYearInfo.forEach((yearInfo) => {
            data.push({
              // For per-quarter sources (PSF), include quarter so multiple
              // quarter snapshots of the same year don't collide on dataKey.
              id: `${key}-${yearInfo.year}${
                yearInfo.quarter ? `-q${yearInfo.quarter}` : ""
              }`,
              dataSetKey: key,
              dataSetLabel: DATA_SET_LABELS[key],
              year: yearInfo.year,
              quarter: yearInfo.quarter ?? null,
              lastUpdated: yearInfo.lastUpdated,
              count: yearInfo.count,
            });
          });
        }
      }
    );

    return data;
  }, [sourceDataStatus]);

  const dataSetBodyTemplate = (rowData: ITableRowData) => {
    return (
      <div className="flex gap-2 align-items-center">
        <i className="pi pi-file" style={{ color: "var(--primary-color)" }} />
        <span className="file-name">{rowData.dataSetLabel}</span>
      </div>
    );
  };

  const yearBodyTemplate = (rowData: ITableRowData) => {
    // For per-quarter sources (PSF), append the quarter for clarity (e.g. "2026 Q2").
    if (rowData.quarter) {
      return `${rowData.year} Q${rowData.quarter}`;
    }
    return rowData.year;
  };

  const lastUpdatedBodyTemplate = (rowData: ITableRowData) => {
    if (!rowData.lastUpdated) return "-";
    return parseDate(rowData.lastUpdated);
  };
  const countBodyTemplate = (rowData: ITableRowData) => {
    if (rowData.count === null) return "-";
    return rowData.count.toLocaleString();
  };

  /**
   * The original uploads aren't stored, so this asks the API to rebuild a CSV
   * from the parsed rows for this data set + year (+ quarter for PSF) and
   * streams it down as a file.
   */
  const handleDownload = async (rowData: ITableRowData) => {
    setDownloadingId(rowData.id);
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = { Authorization: `Bearer ${token}` };
      if (currentAccount) {
        headers.account = currentAccount;
      }

      const params = new URLSearchParams({
        dataSet: rowData.dataSetKey,
        year: String(rowData.year),
      });
      if (rowData.quarter) {
        params.set("quarter", String(rowData.quarter));
      }

      const response = await fetch(
        `${apiUrl}${
          apiUrl.endsWith("/") ? "" : "/"
        }base-rate-files/source/export?${params.toString()}`,
        { headers }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${rowData.dataSetKey}-${rowData.year}${
        rowData.quarter ? `-Q${rowData.quarter}` : ""
      }.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Source data download error:", error);
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to download source data",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadBodyTemplate = (rowData: ITableRowData) => (
    <Button
      icon="pi pi-download"
      className="p-button-rounded p-button-text p-button-sm"
      onClick={() => handleDownload(rowData)}
      loading={downloadingId === rowData.id}
      disabled={downloadingId !== null}
      tooltip="Download CSV"
      tooltipOptions={{ position: "left" }}
    />
  );

  if (tableData.length === 0) {
    return null;
  }

  return (
    <DataTable
      value={tableData}
      className="p-datatable-sm"
      emptyMessage="No data available"
      dataKey="id"
    >
      <Column
        field="dataSetLabel"
        header="Data Set"
        body={dataSetBodyTemplate}
        style={{ minWidth: "200px" }}
      />
      <Column
        field="year"
        header="Year"
        body={yearBodyTemplate}
        style={{ width: "100px" }}
      />
      <Column
        field="lastUpdated"
        header="Last Updated"
        body={lastUpdatedBodyTemplate}
        style={{ width: "150px" }}
      />
      <Column
        field="count"
        header="Rows Count"
        body={countBodyTemplate}
        style={{ width: "120px" }}
      />
      <Column
        header="Download"
        body={downloadBodyTemplate}
        style={{ width: "110px", textAlign: "center" }}
      />
    </DataTable>
  );
}

export default SourceDataTable;
